import { GEM_COLUMNS } from './60-gem-powers.mjs';
import { GAME_MOVE_LIMIT, GAME_PALETTE, cell, option, adjacent } from './53-game-primitives.mjs';
import {
  createBoardGame,
  moveBoardGame,
  blockPlacement,
  clusterGroups,
  clusterBurst,
  CLUSTER_COLUMNS,
  MERGE_COLUMNS,
  MERGE_TARGET,
  mergeSpawnBase,
} from './56-score-board-games.mjs';
import { createPuzzle, movePuzzle, pipeNetwork } from './57-score-puzzles.mjs';

const BOARD_GAMES = new Set(['gem-swap', 'cluster-pop', 'soft-merge', 'block-fit']);
const SYMBOLS = ['●', '▲', '◆', '★', '■', '✦', '♥', '⬟'];
const GEM_NAMES = [
  'Mint leaf',
  'Coral apple',
  'Violet blossom',
  'Honey acorn',
  'Blue dewdrop',
  'Raspberry twins',
];
const GEM_GLYPHS = ['❧', '♥', '✿', '♠', '●', '♣'];
const NAMES = ['circle', 'triangle', 'diamond', 'star', 'square', 'spark', 'heart', 'hexagon'];
const symbolCell = (value, i, extra = {}) =>
  cell(i, `${SYMBOLS[value]} · ${NAMES[value]}`, {
    value,
    colour: GAME_PALETTE[value % 6],
    glyph: SYMBOLS[value],
    ...extra,
  });

/** Initialize original finite challenges. Mechanics owners share only seeded pure
 * primitives; score, goals and progression never depend on browser wall time. */
export function createScore(state) {
  if (BOARD_GAMES.has(state.gameId)) createBoardGame(state);
  else createPuzzle(state);
}

/** Apply a legal move to the engine-owned clone; selection and hint state cannot
 * earn points independently of the completed game transition. */
export function moveScore(state, move) {
  if (BOARD_GAMES.has(state.gameId)) moveBoardGame(state, move);
  else movePuzzle(state, move);
}

function progress(current, total, label) {
  return { current, total, label };
}

/** Count solved staged puzzles while their canonical stage remains held.
 * Next advances the stage, not the completed count; action exhaustion alone
 * cannot turn an unfinished puzzle into a completed one. */
function completedStages(state) {
  return state.data.stage - (state.data.stageComplete === true || state.completed === true ? 0 : 1);
}

/** Advisory accessible view, including bounded last-transition animation events.
 * Symbols accompany colours. Hidden memory values and generated solutions are
 * never projected. Disabled cells reject illegal input, not reveal puzzle answers. */
export function scoreView(state) {
  const d = state.data;
  const view = {
    columns: 4,
    cells: [],
    options: [],
    goal: '',
    status: '',
    stage: d.level || d.stage || 1,
    chain: d.chain || 0,
    combo: d.combo || 0,
    events: d.events || [],
  };
  switch (state.gameId) {
    case 'gem-swap':
      view.columns = GEM_COLUMNS;
      view.cells = d.board.map((v, i) => {
        const special = d.specials[i];
        const name =
          special === 'nova'
            ? 'Bloom · match to burst nearby fruit'
            : special === 'spectrum'
              ? 'Harvest · swap to clear a colour'
              : special === 'row'
                ? 'Row Prism'
                : special === 'col'
                  ? 'Column Prism'
                  : special === 'burst'
                    ? 'Burst Prism'
                    : '';
        return symbolCell(v, i, {
          selected: d.selected === i,
          special,
          glyph: GEM_GLYPHS[v],
          label: `${GEM_GLYPHS[v]} · ${GEM_NAMES[v]}${name ? ` · ${name}` : ''}`,
        });
      });
      view.goal = 'Tap two neighbours or swipe to match 3. Match 4 for a Bloom; 5 for a Harvest.';
      view.progress = progress(d.turns, 20, 'swaps');
      view.status = `Level ${d.level}/3 · ${20 - d.turns} swaps left${d.combo > 1 ? ` · ${d.combo} clear streak` : ''}.`;
      break;
    case 'cluster-pop': {
      view.columns = CLUSTER_COLUMNS;
      const bursts = clusterGroups(d.board).map((cells) => clusterBurst(d, cells[0])),
        byCell = new Map(bursts.flatMap((burst) => burst.cells.map((i) => [i, burst]))),
        best = bursts.reduce(
          (largest, burst) => (!largest || burst.size > largest.size ? burst : largest),
          null,
        );
      view.cells = d.board.map((v, i) => {
        const burst = byCell.get(i);
        return symbolCell(v, i, {
          disabled: !burst,
          clusterSize: burst?.size || 1,
          previewScore: burst?.points || 0,
        });
      });
      view.burstHint = state.completed ? null : best;
      view.chainGoal = 4;
      view.chainCap = 5;
      view.goal = 'Clear two or more touching bubbles of one colour; four build a chain.';
      view.progress = progress(d.turns, 20, 'groups');
      view.status = bursts.length
        ? `Level ${d.level}/3 · ${20 - d.turns} groups left.`
        : 'No groups remain · round complete.';
      break;
    }
    case 'soft-merge':
      view.columns = MERGE_COLUMNS;
      view.cells = d.board.map((v) => ({
        label: v ? String(v) : 'Empty',
        value: v,
        disabled: true,
        colour: v ? GAME_PALETTE[Math.min(5, Math.log2(v) - 1)] : undefined,
      }));
      view.options = ['Left', 'Right', 'Up', 'Down'].map((label, i) => option(i, label));
      view.goal = 'Merge toward8192 on a six-by-six arcade board.';
      view.progress = progress(
        Math.min(MERGE_TARGET, Math.max(...d.board)),
        MERGE_TARGET,
        'largest tile',
      );
      view.status = d.milestone
        ? '8192 reached · complete.'
        : `Largest tile ${Math.max(...d.board)} · Refill ${mergeSpawnBase(Math.max(...d.board))}/${mergeSpawnBase(Math.max(...d.board)) * 2} · ${GAME_MOVE_LIMIT - state.moves} actions left.`;
      break;
    case 'block-fit':
      view.columns = 6;
      view.cells = d.board.map((v, i) =>
        cell(i, v ? 'Filled' : 'Empty', {
          value: v,
          colour: v ? GAME_PALETTE[(v - 1) % 6] : undefined,
          glyph: v ? '■' : '',
          selected: false,
          disabled: !blockPlacement(d, i).length,
          previewCells: blockPlacement(d, i),
        }),
      );
      view.options = [
        option(0, 'Rotate piece'),
        ...d.tray.map((piece, i) => ({
          ...option(i + 1, `Piece ${i + 1}`),
          piece,
          selected: d.selected === i,
          disabled: !piece,
        })),
      ];
      view.goal = 'Clear rows or columns. Clear the whole board for a bonus.';
      view.progress = progress(d.turns, 30, 'pieces');
      view.status = `Level ${d.level}/3 · ${30 - d.turns} pieces left${d.combo ? ` · ${d.combo} clear streak` : ''}.`;
      view.tray = d.tray;
      view.piece = d.piece;
      break;
    case 'colour-path': {
      const occupied = d.paths.flat();
      const path = d.active >= 0 ? d.paths[d.active] : [];
      view.cells = d.board.map((_, i) => {
        const pair = d.pairs.findIndex((p) => p.includes(i)),
          owner = d.paths.findIndex((p) => p.includes(i)),
          colour = pair >= 0 ? pair : owner;
        const allowed = !path.length
          ? pair >= 0
          : path.includes(i) ||
            (adjacent(path.at(-1), i, 4) &&
              (!occupied.includes(i) || owner === d.active) &&
              (pair < 0 || pair === d.active));
        return cell(
          i,
          pair >= 0
            ? `Connect pair ${pair + 1} · ${SYMBOLS[pair]}`
            : owner >= 0
              ? `Line ${owner + 1}`
              : 'Empty square',
          {
            value: pair >= 0 ? pair + 1 : 0,
            glyph: pair >= 0 ? SYMBOLS[pair] : '',
            colour: colour >= 0 ? GAME_PALETTE[colour] : undefined,
            selected: owner >= 0,
            disabled: !allowed,
            path: owner >= 0,
            endpoint: pair >= 0,
          },
        );
      });
      view.options = [{ ...option(0, 'Redraw current line'), disabled: d.active < 0 }];
      view.goal = 'Join matching symbols without crossing. Fill every square.';
      view.progress = progress(completedStages(state), 3, 'puzzles');
      view.status = `Puzzle ${d.stage} of 3 · ${d.pairs.length} pairs · ${occupied.length}/16 squares linked.`;
      break;
    }
    case 'memory-pairs':
      view.cells = d.board.map((v, i) => {
        const visible = d.opened.includes(i) || d.found.includes(i);
        return cell(i, visible ? `${SYMBOLS[v]} · ${NAMES[v]}` : 'Hidden card', {
          value: visible ? v : null,
          colour: visible ? GAME_PALETTE[v % 6] : undefined,
          glyph: visible ? SYMBOLS[v] : undefined,
          selected: visible,
          matched: d.found.includes(i),
          disabled: visible || d.opened.length >= 2,
        });
      });
      if (d.opened.length === 2) view.options = [option(0, 'Hide missed pair')];
      view.goal = 'Remember the cards. Match pairs to build a streak.';
      view.progress = progress(d.found.length / 2, d.board.length / 2, 'pairs');
      view.status = `Garden ${d.stage} of 3 · ${d.found.length / 2}/${d.board.length / 2} pairs.`;
      view.autoDismiss =
        d.opened.length === 2 ? { move: { type: 'option', index: 0 }, delayMs: 900 } : null;
      break;
    case 'tile-slide':
      view.columns = 3;
      view.cells = d.board.map((v, i) =>
        cell(i, v || 'Empty', { value: v, disabled: !adjacent(i, d.board.indexOf(0), 3) }),
      );
      view.goal = 'Arrange 1 to 8, leaving the bottom-right empty.';
      view.progress = progress(completedStages(state), 3, 'puzzles');
      view.status = `Puzzle ${d.stage} of 3 · ${d.steps} ${d.steps === 1 ? 'move' : 'moves'}.`;
      view.target = [1, 2, 3, 4, 5, 6, 7, 8, 0];
      break;
    case 'rotation-loop': {
      const network = pipeNetwork(d),
        glyphs = {
          0: '·',
          1: '╵',
          2: '╶',
          4: '╷',
          8: '╴',
          3: '└',
          6: '┌',
          12: '┐',
          9: '┘',
          5: '│',
          10: '─',
          7: '├',
          11: '┴',
          13: '┤',
          14: '┬',
          15: '┼',
        };
      view.columns = 3;
      view.cells = d.board.map((v, i) =>
        cell(
          i,
          `${i === d.source ? 'Source · ' : i === d.destination ? 'Destination · ' : ''}Row ${Math.floor(i / 3) + 1}, column ${(i % 3) + 1} · Connections ${
            [
              [1, 'north'],
              [2, 'east'],
              [4, 'south'],
              [8, 'west'],
            ]
              .filter(([port]) => v & port)
              .map(([, name]) => name)
              .join(', ') || 'none'
          }${network.lit.includes(i) ? ' · lit' : ''}`,
          {
            value: v,
            glyph: glyphs[v],
            lit: network.lit.includes(i),
            source: i === d.source,
            destination: i === d.destination,
            selected: false,
            colour: network.lit.includes(i) ? 0 : undefined,
          },
        ),
      );
      view.goal = 'Rotate pipes. Light the whole network without leaks.';
      view.progress = progress(network.lit.length, 9, 'lit pipes');
      view.status = `Network ${d.stage} of 3 · ${network.lit.length}/9 lit.`;
      break;
    }
    case 'number-trail':
      view.columns = d.board.length === 9 ? 3 : 4;
      view.cells = d.board.map((v, i) =>
        cell(i, v, { value: v, selected: v < d.next, disabled: v < d.next }),
      );
      view.goal = 'Find the numbers in order. Keep a clean chain.';
      view.progress = progress(d.next - 1, d.board.length, 'numbers');
      view.status = `Find ${d.next} · meadow ${d.stage} of 3.`;
      break;
    case 'spot-pair':
      view.columns = d.board.length === 9 ? 3 : 4;
      view.cells = d.board.map((v, i) =>
        cell(i, `${SYMBOLS[v % 6]} · ${NAMES[v % 6]} · colour ${Math.floor(v / 6) + 1}`, {
          value: v,
          glyph: SYMBOLS[v % 6],
          colour: GAME_PALETTE[Math.floor(v / 6)],
          selected: d.selected === i,
        }),
      );
      view.goal = 'Find the two with the same shape and colour.';
      view.progress = progress(completedStages(state), 10, 'pairs');
      view.status = `Pair ${d.stage} of 10${d.combo > 1 ? ` · ${d.combo} streak` : ''}.`;
      break;
  }
  view.stageComplete = d.stageComplete === true && !state.completed;
  view.advanceAction = view.stageComplete ? { type: 'option', index: 0 } : null;
  if (view.stageComplete) {
    view.cells = view.cells.map((item) => ({ ...item, disabled: true }));
    view.options = [{ ...option(0, 'Next level'), advanceAction: true }];
    view.autoDismiss = null;
    view.status = `Level ${d.stage} complete · next level ready.`;
  }
  if (state.completed) {
    view.completionReason =
      state.gameId === 'soft-merge'
        ? d.milestone
          ? 'goal-reached'
          : 'stalled'
        : state.gameId === 'block-fit'
          ? d.turns >= 30
            ? 'round-complete'
            : 'stalled'
          : state.gameId === 'cluster-pop'
            ? d.turns >= 20
              ? 'round-complete'
              : 'stalled'
            : state.gameId === 'gem-swap'
              ? 'round-complete'
              : 'solved';
  }
  return view;
}
